import { normalizeUnknownValue } from "./utils";

const STATE_CODE_TO_NAME: Record<string, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia",
};

export const STATE_NAME_TO_CODE = Object.entries(STATE_CODE_TO_NAME).reduce<Record<string, string>>(
  (acc, [code, name]) => {
    acc[name.toLowerCase()] = code;
    return acc;
  },
  {},
);

export function expandStateCode(code?: string | null): string | null {
  if (!code) return null;
  const cleaned = stripCountrySuffix(code).trim();
  if (!cleaned) {
    return null;
  }
  const normalized = cleaned.toUpperCase();
  return STATE_CODE_TO_NAME[normalized] ?? cleaned;
}

export function compactCityLabel(city?: string | null, state?: string | null): string {
  const normalizedCity = normalizeCityName(city);
  const normalizedState = normalizeStateCode(state);
  if (normalizedState) {
    return normalizedCity ? `${normalizedCity}, ${normalizedState}` : normalizedState;
  }
  return normalizedCity;
}

export function formatCityHeader(city?: string | null, state?: string | null, country = "US"): string {
  const normalizedCity = normalizeCityName(city) || "Unknown city";
  const normalizedState = normalizeStateCode(state);
  const expanded = expandStateCode(normalizedState);
  const stateLabel = expanded ? `, ${expanded}` : normalizedState ? `, ${normalizedState}` : "";
  const normalizedCountry = (country ?? "US").trim().toUpperCase() || "US";
  return `${normalizedCity}${stateLabel} [${normalizedCountry}]`;
}

export { STATE_CODE_TO_NAME };

function stripCountrySuffix(value?: string | null): string {
  if (!value) return "";
  const cleaned = value.trim();
  if (!cleaned) return "";
  return cleaned.replace(/\s*(?:,)?\s*(?:\(|\[)?(?:US|USA|United States(?: of America)?)\s*(?:\)|\])?\s*$/i, "").trim();
}

function normalizeCityName(city?: string | null): string {
  const value = normalizeUnknownValue(city);
  if (!value) return "";
  const cleaned = value.replace(/\s+/g, " ").trim();
  if (!cleaned) return "";
  const lower = cleaned.toLowerCase();
  return lower.replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizeStateCode(state?: string | null): string {
  const cleaned = stripCountrySuffix(state);
  if (!cleaned) return "";
  const upper = cleaned.toUpperCase();
  if (upper.length === 2) {
    return upper;
  }
  const mapped = STATE_NAME_TO_CODE[cleaned.toLowerCase()];
  return mapped ?? upper;
}
